import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useFAQContent } from '../hooks/useCMSContent';
import { ChevronDown } from 'lucide-react';

const defaultFaqs = [
  { question: 'Do I need to understand AI or have a tech team?', answer: "No. You describe the problem. We handle every technical detail — platforms, integrations, testing, and deployment." },
  { question: 'How much does it cost?', answer: "Most solutions range from $1,500 to $5,000+. We give you a clear, fixed-price quote before any work begins." },
  { question: 'How long until I see results?', answer: "Most clients see results within 2-4 weeks. A chatbot captures leads from day one. An automation saves hours from its first run." },
  { question: "What if AI isn't right for my business yet?", answer: "We'll tell you. If AI won't move the needle right now, we'll say so and suggest what to focus on instead." },
  { question: 'What kinds of AI do you build?', answer: 'Chatbots, automated follow-up workflows, appointment booking, AI content pipelines, review management, reporting dashboards, and custom integrations.' },
  { question: 'Will it work with my existing tools?', answer: "Yes. We integrate with what you already use — Wix, WordPress, Google Calendar, HubSpot, Zapier, and industry-specific software." }
];

const FAQSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [openIndex, setOpenIndex] = useState(0);
  const { faq: cmsData } = useFAQContent();

  const faqs = cmsData?.items || defaultFaqs;
  const badge = cmsData?.badge || 'FAQ';
  const title = cmsData?.title || 'Common Questions';

  return (
    <section id="faq" className="py-20 bg-[#0A0A0A] overflow-hidden">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 40 }}
          animate={headerVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF6B00] mb-5">
            {badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            {title}
          </h2>
        </motion.div>

        <div className="bg-[#0A0A0A] rounded-2xl border border-white/[0.08] p-5 md:p-7 card-glow-hover">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-white/[0.08] last:border-0" data-testid={`faq-item-${index}`}>
              <button
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                className="w-full flex items-center justify-between py-4 text-left group"
                data-testid={`faq-toggle-${index}`}
              >
                <span className="text-white font-medium text-sm pr-4 group-hover:text-[#A1A1AA] transition-colors">{faq.question}</span>
                <motion.div animate={{ rotate: openIndex === index ? 180 : 0 }} transition={{ duration: 0.2 }} className="flex-shrink-0">
                  <ChevronDown className="w-4 h-4 text-[#71717A]" />
                </motion.div>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p className="pb-4 text-[#A1A1AA] text-sm leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
